import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { PortalKopf, PortalSeite } from '../components/portal/PortalKopf';

const inp = 'mt-1 w-full h-11 px-3 rounded-[10px] border border-gray-200 bg-white text-sm text-[#1B3A5C] focus:outline-none focus:border-[#b8893d] focus:ring-2 focus:ring-[#b8893d]/15';

const Feld = ({ f, wert, onChange }) => (
  <label className="block text-xs font-medium text-[#34536b]" data-testid={`angaben-feld-${f.key}`}>{f.label}
    {f.typ === 'anrede' ? (
      <select value={wert} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={`angaben-input-${f.key}`}>
        <option value="" label="Bitte wählen" /><option value="Herr" label="Herr" /><option value="Frau" label="Frau" /><option value="Divers" label="Divers" />
      </select>
    ) : <input type="text" inputMode={f.typ === 'datum' ? 'numeric' : undefined} placeholder={f.typ === 'datum' ? 'TT.MM.JJJJ' : ''} value={wert} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={`angaben-input-${f.key}`} />}
  </label>
);

// Kurzformular: nur die von der Kanzlei nachgeforderten, noch fehlenden Angaben
export default function PortalAngaben() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [w, setW] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const laden = () => api.get(`/portal/nachforderung/${id}`).then((r) => setD(r.data)).catch((e) => setD({ fehler: e?.response?.data?.detail || 'Anfrage nicht gefunden' }));
  useEffect(() => { laden(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const senden = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    const werte = Object.fromEntries(Object.entries(w).map(([k, v]) => {
      const m = d.felder.find((f) => f.key === k)?.typ === 'datum' && String(v).trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      return [k, m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : v];
    }));
    try { const r = await api.post(`/portal/nachforderung/${id}`, { werte }); setMsg({ ok: true, text: r.data.offen.length ? `Danke! Noch offen: ${r.data.offen.join(', ')}` : 'Vielen Dank – Ihre Angaben sind bei der Kanzlei angekommen.' }); setW({}); laden(); } catch (x) { setMsg({ text: x?.response?.data?.detail || 'Speichern fehlgeschlagen' }); }
    setBusy(false);
  };
  const fertig = d && !d.fehler && (d.status !== 'offen' || !d.felder.length);
  return (
    <PortalSeite testId="portal-angaben">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-28">
        <PortalKopf kicker="Erfassungsbogen" titel="Fehlende Angaben ergänzen" />
        <div className="max-w-xl mx-auto px-5 mt-8">
          {!d ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div>
            : d.fehler ? <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="angaben-fehler"><AlertTriangle className="w-4 h-4" />{d.fehler}</p>
            : fertig ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center space-y-3" data-testid="angaben-fertig">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <p className="font-serif text-2xl text-[#1B3A5C]">Alles erledigt</p>
                <p className="text-sm text-[#56616D]">{msg?.text || 'Ihre Angaben liegen der Kanzlei vollständig vor.'}</p>
                <Link to="/meine-akten" className="inline-flex h-10 items-center px-5 rounded-[10px] bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280]" data-testid="angaben-zum-chat">Zurück zu den Nachrichten</Link>
              </div>
            ) : (
              <form onSubmit={senden} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4" data-testid="angaben-form">
                <p className="text-sm text-[#56616D]">Die Kanzlei benötigt noch {d.felder.length} Angabe{d.felder.length === 1 ? '' : 'n'} von Ihnen. Ihre Eingaben werden direkt in Ihre Mandantendaten übernommen.</p>
                <div className="grid sm:grid-cols-2 gap-4">{d.felder.map((f) => <Feld key={f.key} f={f} wert={w[f.key] || ''} onChange={(v) => setW((s) => ({ ...s, [f.key]: v }))} />)}</div>
                {msg && <p className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="angaben-meldung">{msg.text}</p>}
                <button type="submit" disabled={busy} className="w-full h-11 rounded-[10px] bg-[#1B3A5C] text-white text-sm font-medium hover:bg-[#2A5280] disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="angaben-senden">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Angaben senden</button>
              </form>
            )}
        </div>
      </main>
    </PortalSeite>
  );
}
